import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import ExportLabel from '../../../components/common/ExportLabel/Export';
import './Countries.css';
import LargeDetailBox from '../../../components/common/DetailBox/LargeDetailBox';
import SmallDetailBox from '../../../components/common/DetailBox/SmallDetailBox';
import ErrorNotification from '../../../components/common/AlertBox/ErrorNotification';
import authenticatedAxios from '../../../authenticatedAxios';
import { baseurl } from '../../../api';

const CountryDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [country, setCountry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchCountry = async () => {
      try {
        const response = await authenticatedAxios.get(`${baseurl}localization/country/?id=${id}`);
        if (response.data && response.data.data) {
          setCountry(response.data.data);
        } else {
          setErrors({ general: 'Country not found.' });
        }
      } catch (error) {
        console.error('Error fetching country detail:', error);
        if (error?.response?.data?.error_code === 1001) {
          navigate('/permission-denied');
        } else {
          setErrors({ general: 'Failed to load country details.' });
        }
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCountry();
    }
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="whole-container">
        <p className="common-text fts-14 ftw-4" style={{ padding: '20px' }}>Loading country details...</p>
      </div>
    );
  }

  if (!country) {
    return (
      <div className="whole-container">
        <div className="top-heading">
          <PageHeader
            pageName="Localization / Countries /"
            nestedpageName="Detail"
            linkTo="/countries"
          />
        </div>
        <div className="page-container">
          <ErrorNotification errors={errors} marginBottom="16px" />
          <p className="common-text fts-14 ftw-4">Country not found.</p>
        </div>
      </div>
    );
  }

  const flagUrl = country.country_flag && typeof country.country_flag === 'string'
    ? `${baseurl.replace(/\/$/, '')}${country.country_flag.startsWith('/') ? '' : '/'}${country.country_flag}`
    : null;

  return (
    <div className="whole-container">
      <div className="top-heading">
        <PageHeader
          pageName="Localization / Countries /"
          nestedpageName="Detail"
          linkTo="/countries"
        />
        <div className="export-container">
          <ExportLabel />
        </div>
      </div>

      <div className="page-container">
        <ErrorNotification errors={errors} marginBottom="16px" />

        {/* Country Details Card */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">Country Details</span>
            <button
              className="popup-add-button common-text white fts-15 ftw-5"
              onClick={() => navigate(`/countries/edit/${country.id}`)}
            >
              Edit Country
            </button>
          </div>

          <div className="card-body">
            {flagUrl && (
              <div>
                <label className="detail-box-label fts-12 lt-gray" style={{ display: 'block', marginBottom: '6px' }}>
                  Country Flag
                </label>
                <div className="country-detail-flag-wrapper">
                  <img
                    src={flagUrl}
                    alt={`${country.name} flag`}
                    className="country-detail-flag-img"
                  />
                </div>
              </div>
            )}

            <LargeDetailBox label="Country Name" value={country.name || 'N/A'} />

            <div className="row-2">
              <SmallDetailBox label="ISO 3166 Code" value={country.iso_code || 'N/A'} />
              <SmallDetailBox label="Country Code" value={country.country_code || 'N/A'} />
            </div>

            <LargeDetailBox
              label="Currency"
              value={
                country.currency
                  ? `${country.currency.name || ''}${country.currency.symbol ? ` (${country.currency.symbol})` : ''}`
                  : 'N/A'
              }
            />
          </div>
        </div>

        {/* Active Plans Card */}
        <div className="features-card">
          <div className="card-header">
            <span className="card-title">Active Plans</span>
          </div>
          <div className="country-pills-container">
            {country.plans && country.plans.length > 0 ? (
              country.plans.map((plan) => (
                <span
                  key={plan.id}
                  className="country-pill clickable"
                  onClick={() => navigate(`/our-plans/detail/${plan.id}`)}
                  title="Click to view plan details"
                >
                  {plan.plan_name || plan.display_name}
                </span>
              ))
            ) : (
              <p className="common-text fts-14 lt-gray">No active plans assigned</p>
            )}
          </div>
        </div>

        {/* Timezones Card */}
        <div className="features-card">
          <div className="card-header">
            <span className="card-title">Timezones</span>
          </div>
          <div className="country-pills-container">
            {country.timezones && country.timezones.length > 0 ? (
              country.timezones.map((tz) => (
                <span key={tz.id} className="country-pill">
                  {tz.name} ({tz.timezone})
                </span>
              ))
            ) : (
              <p className="common-text fts-14 lt-gray">No timezones assigned</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CountryDetail;
